import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Annotation, Compartment, EditorState } from '@codemirror/state';
import { EditorView, placeholder as cmPlaceholder } from '@codemirror/view';
import { buildMinimalSetup } from './basic-setup';
// Marks transactions that originated from the controlled-value sync so we
// don't echo them back through onChange, avoiding potential feedback loops.
const ExternalChange = Annotation.define();
const MinimalCodeMirror = forwardRef((props, ref) => {
    const { value = '', extensions = [], onChange, onFocus, onBlur, onCreateEditor, theme, editable = true, readOnly, placeholder, height, basicSetup, style, className, } = props;
    const containerRef = useRef(null);
    const viewRef = useRef(null);
    const extCompartment = useRef(new Compartment());
    const editCompartment = useRef(new Compartment());
    // Stable references to callbacks so we don't rebuild the editor on every render
    const onChangeCb = useRef(onChange);
    const onFocusCb = useRef(onFocus);
    const onBlurCb = useRef(onBlur);
    useEffect(() => { onChangeCb.current = onChange; });
    useEffect(() => { onFocusCb.current = onFocus; });
    useEffect(() => { onBlurCb.current = onBlur; });
    // Getter so the ref value stays live even after React batches useImperativeHandle
    useImperativeHandle(ref, () => ({
        get view() { var _a; return (_a = viewRef.current) !== null && _a !== void 0 ? _a : undefined; },
    }), []);
    // Create editor once on mount
    useEffect(() => {
        if (!containerRef.current)
            return;
        const heightTheme = height
            ? EditorView.theme({ '&': { height }, '.cm-scroller': { overflow: 'auto' } })
            : [];
        const view = new EditorView({
            state: EditorState.create({
                doc: value,
                extensions: [
                    buildMinimalSetup(basicSetup),
                    heightTheme,
                    [],
                    placeholder ? cmPlaceholder(placeholder) : [],
                    editCompartment.current.of([
                        EditorView.editable.of((editable !== null && editable !== void 0 ? editable : true) && !readOnly),
                        EditorState.readOnly.of(!!readOnly),
                    ]),
                    extCompartment.current.of(extensions),
                    EditorView.updateListener.of((update) => {
                        var _a, _b, _c;
                        if (update.docChanged && !update.transactions.some(t => t.annotation(ExternalChange))) {
                            (_a = onChangeCb.current) === null || _a === void 0 ? void 0 : _a.call(onChangeCb, update.state.doc.toString());
                        }
                        if (update.focusChanged) {
                            update.view.hasFocus ? (_b = onFocusCb.current) === null || _b === void 0 ? void 0 : _b.call(onFocusCb) : (_c = onBlurCb.current) === null || _c === void 0 ? void 0 : _c.call(onBlurCb);
                        }
                    }),
                ],
            }),
            parent: containerRef.current,
        });
        viewRef.current = view;
        onCreateEditor === null || onCreateEditor === void 0 ? void 0 : onCreateEditor(view);
        return () => {
            view.destroy();
            viewRef.current = null;
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    // Sync controlled value without triggering onChange
    useEffect(() => {
        const view = viewRef.current;
        if (!view)
            return;
        const cur = view.state.doc.toString();
        if (cur !== value) {
            view.dispatch({
                changes: { from: 0, to: cur.length, insert: value },
                annotations: ExternalChange.of(true),
            });
        }
    }, [value]);
    // Reconfigure dynamic extensions
    useEffect(() => {
        var _a;
        (_a = viewRef.current) === null || _a === void 0 ? void 0 : _a.dispatch({
            effects: extCompartment.current.reconfigure(extensions),
        });
    }, [extensions]);
    // Reconfigure editable / readOnly
    useEffect(() => {
        var _a;
        (_a = viewRef.current) === null || _a === void 0 ? void 0 : _a.dispatch({
            effects: editCompartment.current.reconfigure([
                EditorView.editable.of((editable !== null && editable !== void 0 ? editable : true) && !readOnly),
                EditorState.readOnly.of(!!readOnly),
            ]),
        });
    }, [editable, readOnly]);
    return React.createElement("div", { ref: containerRef, className: `uxpcore_cm_wrapper${className ? ` ${className}` : ''}`, style: style });
});
MinimalCodeMirror.displayName = 'MinimalCodeMirror';
export default MinimalCodeMirror;
